import React from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, Divider, LinearProgress, Paper, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useUser } from '@store';
import { formatDateTime } from '../utils/guestRideUtils';
import { approveGuestRide, denyGuestRide, ridesApiError } from '../services/ridesApi';
import { useGetGuestRideApprovalQuery } from './getGuestRideApproval.query.generated';
import { approvalBannerFor, formatFare, formatTrip, type ApprovalEstimate } from './approvalView';

const FARE_CAVEAT =
  'Estimated fare. Final rideshare pricing may change before booking. Approval authorizes this trip, not a guaranteed final price.';

const Row = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography variant='body2' sx={{ color: 'text.secondary', flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  ) : null;

/**
 * Where the approver lands from the approval text or email. Built for a phone: the trip, the
 * fare quoted when the ride was called, and Approve and Deny pinned to the bottom of the screen.
 * Anyone at the rooftop can open it; only the named approver gets the buttons.
 */
const GuestRideApprovalPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const guestrideId = Number(id);
  const user = useUser();
  const viewerUserId = user?.profile?.id ? Number(user.profile.id) : null;

  const { data, loading, error, refetch } = useGetGuestRideApprovalQuery({
    variables: { id: guestrideId },
    skip: !Number.isInteger(guestrideId),
    fetchPolicy: 'network-only',
  });

  const [busy, setBusy] = React.useState<'approve' | 'deny' | null>(null);
  const [denying, setDenying] = React.useState(false);
  const [reason, setReason] = React.useState('');

  const ride = data?.guestrides_by_pk;

  const act = async (kind: 'approve' | 'deny') => {
    setBusy(kind);
    try {
      if (kind === 'approve') {
        await approveGuestRide(guestrideId);
        toast.success('Approved. The ride is booking now.');
      } else {
        await denyGuestRide(guestrideId, reason.trim());
        toast.success('Denied. The ride was cancelled.');
      }
    } catch (err) {
      toast.error(ridesApiError(err).message || `Could not ${kind} this ride.`);
    } finally {
      setBusy(null);
      setDenying(false);
      refetch();
    }
  };

  if (loading) return <LinearProgress />;
  if (error || !ride) {
    return (
      <Box sx={{ p: 2, maxWidth: 560, mx: 'auto' }}>
        <Alert severity='error'>We couldn't find this guest ride. It may belong to a rooftop you can't see.</Alert>
      </Box>
    );
  }

  const banner = approvalBannerFor(ride, viewerUserId);
  const estimate = ride.approval_estimate as ApprovalEstimate | null;
  const fare = formatFare(estimate);
  const trip = formatTrip(estimate);

  return (
    <Box sx={{ p: 2, pb: banner.kind === 'decide' ? 14 : 2, maxWidth: 560, mx: 'auto' }}>
      <Typography variant='h6' sx={{ fontWeight: 700, mb: 1.5 }}>
        Guest Ride #{ride.id}
      </Typography>

      {banner.kind === 'decide' && <Alert severity='warning'>This ride is waiting for your approval. Nothing is booked until you approve it.</Alert>}
      {banner.kind === 'waiting' && <Alert severity='info'>Waiting for {banner.approverName} to approve or deny this ride.</Alert>}
      {banner.kind === 'approved' && (
        <Alert severity='success'>Approved{banner.at ? ` ${formatDateTime(banner.at)}` : ''}. The ride books through the usual process.</Alert>
      )}
      {banner.kind === 'denied' && (
        <Alert severity='error'>Denied{banner.reason ? `: ${banner.reason}` : '.'} The ride was cancelled.</Alert>
      )}
      {banner.kind === 'cancelled' && <Alert severity='info'>This ride was cancelled before a decision.</Alert>}
      {banner.kind === 'not-held' && <Alert severity='info'>This ride doesn't need manager approval.</Alert>}

      <Paper variant='outlined' sx={{ mt: 2, p: 2 }}>
        <Row label='Guest' value={ride.guest_name} />
        <Row label='Pickup' value={ride.pickup_address} />
        <Row label='Dropoff' value={ride.dropoff_address} />
        <Row label='Trip' value={trip} />
        <Row label='Ride type' value={(ride.config as { productType?: string } | null)?.productType ?? null} />
        <Row label='RO number' value={ride.reference_num} />
        <Divider sx={{ my: 1 }} />
        <Row label='Requested by' value={ride.created_by} />
        <Row label='Requested' value={ride.approval_requested_at ? formatDateTime(ride.approval_requested_at) : null} />
        <Row label='Approver' value={ride.approver?.display_name} />
      </Paper>

      <Paper variant='outlined' sx={{ mt: 2, p: 2 }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Estimated fare
        </Typography>
        <Typography variant='h5' sx={{ fontWeight: 700 }}>
          {fare ?? 'Not available'}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {FARE_CAVEAT}
        </Typography>
      </Paper>

      {banner.kind === 'decide' && (
        <Paper
          elevation={8}
          sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, p: 2, zIndex: theme => theme.zIndex.appBar }}
        >
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            {denying && (
              <TextField
                fullWidth
                size='small'
                label='Reason (optional)'
                value={reason}
                onChange={e => setReason(e.target.value)}
                inputProps={{ maxLength: 500 }}
                sx={{ mb: 1.5 }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                fullWidth
                variant='outlined'
                color='error'
                disabled={busy !== null}
                onClick={() => (denying ? act('deny') : setDenying(true))}
              >
                {busy === 'deny' ? 'Denying...' : denying ? 'Confirm deny' : 'Deny'}
              </Button>
              <Button fullWidth variant='contained' disabled={busy !== null} onClick={() => act('approve')}>
                {busy === 'approve' ? 'Approving...' : 'Approve'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default GuestRideApprovalPage;
